import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The hover behaviour on a skill tile
 * -------------------------------------------------------------
 * Ported from a GSAP + Tailwind bento-grid component, with the layout, the
 * grid, the dependencies and the purple left behind. What was worth having is
 * the behaviour: a spotlight that follows the cursor, a border that lights up
 * on the side the cursor is on, a slight tilt towards it, and a ripple where
 * the tile is clicked.
 *
 * The original also scattered drifting particles across each card on hover.
 * They were tried and taken out: a dozen glinting dots on a tile this size
 * read as decoration rather than response, and they fought the one thing the
 * effect is for, which is showing where the cursor is.
 *
 * WHY NOT GSAP
 * The original animates every property through a tween library. Everything
 * here is either a CSS custom property the compositor can read directly, or a
 * keyframe animation declared once in the stylesheet — so the work per mouse
 * move is a handful of `setProperty` calls on one element, no library, and
 * nothing to add to the bundle. Nothing in the effect needs a timeline.
 *
 * WHY THE VALUES GO THROUGH CSS VARIABLES
 * Setting React state on pointermove would re-render the tile sixty times a
 * second. The variables are written straight onto the node; only the ripples,
 * which appear once per click, are state.
 */

const TILT_DEGREES = 5
const MAGNET_STRENGTH = 0.035
const RIPPLE_MS = 700

export interface TileRipple {
  id: number
  x: number
  y: number
}

interface Options {
  /** The ripple answers a click, so a tile that opens nothing does not get one. */
  interactive?: boolean
}

export function useTileInteraction<T extends HTMLElement>({ interactive = true }: Options = {}) {
  const ref = useRef<T>(null)
  const [ripples, setRipples] = useState<TileRipple[]>([])
  const timers = useRef<number[]>([])
  const nextId = useRef(0)

  /**
   * Off entirely without a real pointer, and off when the reader has asked for
   * less motion. A tilt that reacts to a finger tap is a glitch, not an
   * effect, and touch devices never see the hover state that starts it.
   */
  const enabled = useRef(true)
  useEffect(() => {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)')
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => {
      enabled.current = fine.matches && !calm.matches
    }
    update()
    fine.addEventListener('change', update)
    calm.addEventListener('change', update)
    const pending = timers.current
    return () => {
      fine.removeEventListener('change', update)
      calm.removeEventListener('change', update)
      pending.forEach(clearTimeout)
    }
  }, [])

  const onPointerMove = useCallback((event: React.PointerEvent<T>) => {
    const node = ref.current
    if (!node || !enabled.current) return

    // Read the box every move rather than caching it on enter: the page can
    // scroll under a still cursor, and a stale box puts the spotlight
    // somewhere the cursor is not.
    const box = node.getBoundingClientRect()
    const x = event.clientX - box.left
    const y = event.clientY - box.top
    const halfW = box.width / 2
    const halfH = box.height / 2

    node.style.setProperty('--tile-x', `${x}px`)
    node.style.setProperty('--tile-y', `${y}px`)
    // Away from the cursor on the vertical axis, towards it on the horizontal:
    // that combination is what reads as the surface leaning to face you.
    node.style.setProperty('--tile-rx', `${((y - halfH) / halfH) * -TILT_DEGREES}deg`)
    node.style.setProperty('--tile-ry', `${((x - halfW) / halfW) * TILT_DEGREES}deg`)
    node.style.setProperty('--tile-mx', `${(x - halfW) * MAGNET_STRENGTH}px`)
    node.style.setProperty('--tile-my', `${(y - halfH) * MAGNET_STRENGTH}px`)
  }, [])

  const onPointerLeave = useCallback(() => {
    const node = ref.current
    if (!node) return
    node.style.setProperty('--tile-rx', '0deg')
    node.style.setProperty('--tile-ry', '0deg')
    node.style.setProperty('--tile-mx', '0px')
    node.style.setProperty('--tile-my', '0px')
  }, [])

  const onPointerDown = useCallback(
    (event: React.PointerEvent<T>) => {
      const node = ref.current
      if (!node || !enabled.current || !interactive) return
      const box = node.getBoundingClientRect()
      const id = nextId.current++
      setRipples((current) => [
        ...current,
        { id, x: event.clientX - box.left, y: event.clientY - box.top },
      ])
      timers.current.push(
        window.setTimeout(() => {
          setRipples((current) => current.filter((ripple) => ripple.id !== id))
        }, RIPPLE_MS),
      )
    },
    [interactive],
  )

  return {
    ref,
    ripples,
    handlers: { onPointerMove, onPointerLeave, onPointerDown },
  }
}
